import React from 'react';
import { can, SubmissionPermissionManager } from '../../helpers/permissions';
import AddUserForm from '../AddUserForm/AddUserForm';
import { Author, Submission } from '../../store/submission/types';
import arrayMove from '../../helpers/arrayMove';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { Checkbox } from '../ui';
import SimpleDialog from '../dialogs/SimpleDialog/SimpleDialog';
import TrashCan from '../../icons/TrashCan';
import styled from 'styled-components';
import { Role } from '../../store/conference/types';
import { useTranslation } from 'react-i18next';
import { getFullName, stringifyOrganization } from '../../helpers/users';
import { useTheme } from '@material-ui/core/styles';
import { useIsMobile } from '../../helpers/window';

interface Props {
  submission: Submission;
  setSubmission: React.Dispatch<React.SetStateAction<Submission>>;
  userRole: Role;
  personId: number;
  authorConfirmation: boolean;
}

const AddAuthorsListForm: React.FC<Props> = ({ submission, setSubmission, userRole, personId, authorConfirmation }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>();
  const isMobile = useIsMobile();

  const primaryAuthorChangeHandler = (index: number) => {
    setSubmission({
      ...submission,
      authors: [
        ...submission.authors.map((author, i) => {
          return {
            ...author,
            is_primary: index == i ? !author.is_primary : author.is_primary,
          };
        }),
      ],
    });
  };

  const currentAuthor = submission.authors.find((author) => author.id === personId);
  const isPrimaryAuthor = new SubmissionPermissionManager(submission).isPrimaryAuthor(currentAuthor?.id ?? 0, userRole);
  const primaryAuthorsCount = submission.authors.filter((author) => author.is_primary).length;
  const isChair = userRole.type == 'chair';

  return (
    <StyledAuthorsListWrapper>
      {can('CAN_EDIT_AUTHORS') && (
        <div className="mt-2">
          <AddUserForm
            users={submission.authors}
            save_handler={(data) => {
              const author: Author = {
                ...data,
                id: 0,
                person_role_id: 0,
                is_primary: !submission.authors.length,
                pending_confirmation: !isChair && authorConfirmation,
              };
              setSubmission({ ...submission, authors: [...submission.authors, author] });
            }}
          />
        </div>
      )}
      {submission.authors.map((author, index) => {
        const fullName = getFullName(author);
        const canTogglePrimary =
          (isChair || (isPrimaryAuthor && author.id !== currentAuthor?.id)) &&
          (!author.is_primary || (author.is_primary && primaryAuthorsCount > 1));
        const canRemove = canTogglePrimary;
        const canOrder = isChair || isPrimaryAuthor;

        return (
          <StyledAuthorsList
            key={index}
            className={`mobileCtrlAuthorCard ${
              authorConfirmation && author.pending_confirmation ? 'mobileCtrlAuthorConfirmation' : ''
            }`}
          >
            {can('CAN_EDIT_AUTHORS') && canOrder && (
              <div className="mr-5 flex mobileCtrlAuthorCardOrder" style={{ width: 32, color: '#0044f0' }}>
                {index > 0 && (
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      const authors = arrayMove([...submission.authors], index, index - 1);
                      setSubmission({ ...submission, authors });
                    }}
                  >
                    <FontAwesomeIcon icon={faSortUp} className="text-xl mr-2" style={{ verticalAlign: -16 }} />
                  </div>
                )}
                {index < submission.authors.length - 1 && (
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      const authors = arrayMove([...submission.authors], index, index + 1);
                      setSubmission({ ...submission, authors });
                    }}
                  >
                    <FontAwesomeIcon icon={faSortDown} className="text-xl" style={{ verticalAlign: 4 }} />
                  </div>
                )}
              </div>
            )}
            <div className="flex mobileCtrlAuthorCardContent" style={{ flexGrow: 1 }}>
              <div className="flex mobileCtrlAuthorCardContentWrapper">
                <span className="font-semibold flex-grow-0">{fullName}</span>
                {!isMobile && <span className="spacer">|</span>}
                <span className="flex-grow-0"> {author.email}</span>
                {!isMobile && <span className="spacer">|</span>}
                <span>{stringifyOrganization(author)}</span>
              </div>

              <StyledCheckboxWrapper className="mobileCtrlAuthorCheckboxWrapper">
                {authorConfirmation && author.pending_confirmation ? (
                  <span style={{ color: theme.palette.error.main, fontWeight: 700 }}>{t('Pending confirmation')}</span>
                ) : (
                  <Checkbox
                    label={t('Managing author')}
                    checked={author.is_primary}
                    disabled={!(can('CAN_EDIT_AUTHORS') && canTogglePrimary)}
                    onChange={() => {
                      if (isPrimaryAuthor && author.id === currentAuthor?.id) {
                        setDialog(
                          <SimpleDialog
                            open={true}
                            handleClose={() => primaryAuthorChangeHandler(index)}
                            handleOk={() => {
                              setSubmission({
                                ...submission,
                                authors: [...submission.authors.filter((e, i) => i != index)],
                              });
                            }}
                            title={t('Remove yourself as primary author?')}
                          >
                            <p className="text-sm mb-2">
                              {t(
                                `Take into account that you'll lose the ability of edit this paper and no longer receive notifications.`,
                              )}
                            </p>
                          </SimpleDialog>,
                        );
                      } else {
                        primaryAuthorChangeHandler(index);
                      }
                    }}
                  />
                )}
              </StyledCheckboxWrapper>
            </div>
            {can('CAN_EDIT_AUTHORS') && canRemove && (
              <div
                className="flex items-center cursor-pointer pl-4 mobileCtrlAuthorCardRemove"
                onClick={() => {
                  setDialog(
                    <SimpleDialog
                      open={true}
                      handleClose={() => setDialog(undefined)}
                      handleOk={() => {
                        setSubmission({
                          ...submission,
                          authors: [...submission.authors.filter((e, i) => i != index)],
                        });
                      }}
                      title={t('Remove author?')}
                    >
                      <p className="text-sm mb-2">{t(`Are you sure you want to remove the author?`)}</p>
                    </SimpleDialog>,
                  );
                }}
              >
                <StyledTrash>
                  <TrashCan />
                </StyledTrash>
              </div>
            )}
          </StyledAuthorsList>
        );
      })}
      {dialog}
    </StyledAuthorsListWrapper>
  );
};

export default AddAuthorsListForm;

const StyledAuthorsListWrapper = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledAuthorsList = styled.div`
  display: flex;
  align-items: center;
  padding: 1.2rem 1.5rem;
  background-color: #e5e6ea;
  border-bottom: 1px solid ${(props) => props.theme.palette.secondary.dark};
  .spacer {
    margin: 0 0.8rem;
  }
`;

const StyledCheckboxWrapper = styled.div`
  flex-grow: 1;
  text-align: right;
  div {
    vertical-align: middle;
  }
`;

const StyledTrash = styled.div`
  margin-top: -3px;
`;
